import { datadogLogs } from '@datadog/browser-logs'

const REQUEST_TIMEOUT_MS = 10000
const PING_INTERVAL_MS = 50

// eslint-disable-next-line no-undef
const frameLogLevel = ['local', 'development'].includes(DEPLOYMENT_ENVIRONMENT) ? 'info' : 'warn'

const frameLogger = datadogLogs.createLogger('authFrameLogger', {
  level: frameLogLevel,
  // eslint-disable-next-line no-undef
  handler: DD_LOGS_HANDLER || 'console',
  context: {
    domain: 'auth',
    component: 'frame-proxy',
  },
})

/**
 * Singleton iframe-based auth proxy for cross-origin authentication requests.
 * Uses postMessage to communicate with an iframe hosted on the identity service
 * that can make requests with HttpOnly cookies.
 *
 * All inbound messages flow through handleMessage, which sets state flags
 * and resolves pending operation requests.
 *
 * Available operations:
 * - 'auth:status' - GET /lender/auth/status
 * - 'auth:login' - POST /lender/auth/login (form-urlencoded)
 * - 'auth:login-code' - POST /lender/auth/login/code (JSON)
 * - 'auth:login-token' - POST /lender/auth/login/token (JSON)
 * - 'auth:token' - POST /lender/auth/token (JSON)
 * - 'auth:logout' - POST /lender/auth/logout
 * - 'auth:accounts' - POST /lender/accounts (JSON)
 */
export class IframeAuthProxy {
  constructor() {
    this.iframe = document.getElementById('auth-proxy-frame')
    this.pendingRequests = new Map()
    this.requestId = 0

    // Handshake state — set by handleMessage
    this.pongReceived = false
    this.operationsReceived = false
    this.availableOperations = []

    // Ready when both pong and operations have been received
    this.ready = false

    window.addEventListener('message', this.handleMessage)

    // Start polling immediately — sends pings/operations requests in the background
    this.pollIntervalId = setInterval(() => {
      if (!this.iframe?.contentWindow) {
        return
      }

      if (!this.pongReceived) {
        frameLogger.debug('[Lender Proxy] Sending ping')
        this.iframe.contentWindow.postMessage({ type: 'proxy-ping' }, IDENTITY_SERVICE_ORIGIN)
      }

      if (!this.operationsReceived) {
        frameLogger.debug('[Lender Proxy] Requesting operations')
        this.iframe.contentWindow.postMessage({ type: 'proxy-list-operations' }, IDENTITY_SERVICE_ORIGIN)
      }

      const pongAndOperationsReceived = this.pongReceived && this.operationsReceived

      // Both received — mark ready and stop polling
      if (pongAndOperationsReceived && !this.ready) {
        this.ready = true
        frameLogger.info('[Lender Proxy] Auth proxy ready')
        clearInterval(this.pollIntervalId)
        this.pollIntervalId = null
      }
    }, PING_INTERVAL_MS)
  }

  /**
   * @param {MessageEvent} event
   */
  handleMessage = (event) => {
    if (event.origin !== window.location.origin &&
      event.origin !== IDENTITY_SERVICE_ORIGIN) {
      return
    }

    const eventData = event.data
    if (!eventData || typeof eventData !== 'object') {
      // frameLogger.debug('[Lender Proxy] Discard event with no data', { handleMessageData: eventData })
      return
    }

    if (!eventData?.type) {
      // frameLogger.debug('[Lender Proxy] Discard event with no type', { handleMessageData: eventData })
      return
    }

    if (eventData.type === 'proxy-ready') {
      // Ignoring proxy-ready since we have a more robust handshake process with ping/pong and operations list
      return
    }

    // Handle pong response — signals the iframe proxy script is alive
    if (eventData.type === 'proxy-pong') {
      frameLogger.info('[Lender Proxy] Received proxy-pong')
      this.pongReceived = true
      return
    }

    // Handle operations list response — signals the proxy is ready to accept requests
    if (eventData.type === 'proxy-operations') {
      frameLogger.info('[Lender Proxy] Received available operations from proxy', { operations: eventData.operations })
      this.availableOperations = eventData.operations || []
      this.operationsReceived = true
      return
    }

    if (eventData.type !== 'proxy-response') {
      frameLogger.debug('[Lender Proxy] Received unknown event', { handleMessageData: eventData })
      return
    }

    // Handle proxy response
    const pending = this.pendingRequests.get(eventData.id)
    if (!pending) {
      frameLogger.warn('[Lender Proxy] Received response with no request', { handleMessageData: eventData })
      return
    }

    this.pendingRequests.delete(eventData.id)
    pending.resolve({
      success: eventData.success,
      status: eventData.status,
      statusText: eventData.statusText,
      data: eventData.data,
      error: eventData.error,
    })
  }

  /**
   * Wait until the proxy is ready. Resolves immediately if already ready,
   * otherwise polls the ready flag.
   * @returns {Promise<void>}
   */
  waitUntilReady() {
    if (this.ready) {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      const checkId = setInterval(() => {
        if (this.ready) {
          clearInterval(checkId)
          resolve()
        }
      }, PING_INTERVAL_MS)
    })
  }

  /**
   * Execute an operation by name.
   * @param {string} operation - The operation name (e.g., 'auth:status')
   * @param {Object} [options] - Request options
   * @param {Object} [options.body] - Request body
   * @param {Object} [options.headers] - Request headers (only allowed headers will be forwarded)
   * @returns {Promise<{status: number, statusText: string, data: any}>}
   */
  async operation(operation, options = {}) {
    const id = this.requestId++

    const responsePromise = new Promise((resolve, reject) => {
      this.pendingRequests.set(id, { resolve, reject })
    })

    await this.waitUntilReady()
    frameLogger.debug('[Lender Proxy] Sending message:', { type: 'proxy-request', operation })
    this.iframe.contentWindow.postMessage({
      type: 'proxy-request',
      id,
      operation,
      body: options.body,
      headers: options.headers,
    }, IDENTITY_SERVICE_ORIGIN)

    // Start timeout after the message is sent, so it only covers the round-trip
    const requestTimeoutId = setTimeout(() => {
      const pending = this.pendingRequests.get(id)
      if (pending) {
        this.pendingRequests.delete(id)
        pending.reject(new Error('Request timeout'))
      }
    }, REQUEST_TIMEOUT_MS)

    return responsePromise.finally(() => {
      clearTimeout(requestTimeoutId)
    })
  }

  /**
   * Check if the proxy is healthy by sending a ping.
   * @returns {Promise<void>}
   */
  async ping() {
    await this.waitUntilReady()
    this.iframe.contentWindow.postMessage({ type: 'proxy-ping' }, IDENTITY_SERVICE_ORIGIN)
  }

  /**
   * Request list of available operations from the proxy.
   */
  async listOperations() {
    await this.waitUntilReady()
    this.iframe.contentWindow.postMessage({ type: 'proxy-list-operations' }, IDENTITY_SERVICE_ORIGIN)
  }
}

// Singleton instance
export const iframeAuthProxy = new IframeAuthProxy()

export default iframeAuthProxy
